import { useCallback, useEffect, useState } from "react";
import type { Player, Team } from "@sportsos/engine";
import { api } from "../api.ts";
import { AddPlayerSheet } from "./AddPlayerSheet.tsx";
import { Profile } from "./Profile.tsx";
import { AVATAR_COLORS, avatarColor, initials } from "./shared.ts";
import { InviteQRSheet } from "./TeamInvite.tsx";
import { useSquadPoll } from "./useSquadPoll.ts";

interface TeamDetailProps {
  readonly teamId: string;
  readonly onBack: (() => void) | null;
}

export function TeamDetail({ teamId, onBack }: TeamDetailProps) {
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  /** The player just added from the sheet, highlighted and scrolled into view above it. */
  const [justAdded, setJustAdded] = useState<string | null>(null);
  /** The squad member whose profile is open, if any. */
  const [openPlayer, setOpenPlayer] = useState<Player | null>(null);

  /**
   * `quiet` re-reads without the loading screen. A refresh after adding a player must not
   * swap the page for "Loading team...", which also unmounted the open sheet mid-entry.
   */
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const res = await api.listTeams();
      const found = res.teams.find((t) => t.id === teamId) ?? null;
      if (!found) setError("Team not found");
      setTeam(found);
    } catch {
      setError("Could not load team");
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  // After the refresh that brings the new row in, so there is a row to scroll to.
  useEffect(() => {
    if (justAdded === null) return;
    document.querySelector(`[data-player-id="${justAdded}"]`)?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [justAdded, team]);

  // Anyone who joins by QR appears while the captain is still looking at the invite.
  useSquadPoll(teamId, showInvite || showAddPlayer, setTeam);

  // A squad member's own history, opened from the row that names him. Rendered in place
  // rather than as a sheet because it is a screen's worth of content with its own back.
  if (openPlayer) {
    return (
      <Profile
        playerId={null}
        viewing={{ playerId: openPlayer.id, displayName: openPlayer.displayName }}
        onBack={() => setOpenPlayer(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="app-page">
        <header className="ds-appbar">
          {onBack && (
            <button className="ds-iconbtn" aria-label="Back" onClick={onBack}>
              <svg viewBox="0 0 24 24"><path d="m14 6-6 6 6 6" /></svg>
            </button>
          )}
          <span className="ds-appbar__body">
            <b className="ds-appbar__title">My team</b>
          </span>
        </header>
        <div style={{ padding: "var(--ds-space-12)", textAlign: "center" }}>
          <span className="ds-caption">Loading team...</span>
        </div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="app-page">
        <header className="ds-appbar">
          {onBack && (
            <button className="ds-iconbtn" aria-label="Back" onClick={onBack}>
              <svg viewBox="0 0 24 24"><path d="m14 6-6 6 6 6" /></svg>
            </button>
          )}
          <span className="ds-appbar__body">
            <b className="ds-appbar__title">My team</b>
          </span>
        </header>
        <div style={{ padding: "var(--ds-space-12)", textAlign: "center" }}>
          <p style={{ color: "var(--ds-danger-600)", marginBottom: "var(--ds-space-4)" }}>
            {error ?? "Team not found"}
          </p>
          <button className="ds-btn ds-btn--secondary" onClick={() => void load()}>Try again</button>
        </div>
      </div>
    );
  }

  const color = avatarColor(team.name);
  const playerCount = team.squad.length;
  const playerLabel = `${playerCount} player${playerCount !== 1 ? "s" : ""}`;

  return (
    <div className="app-page">
      {/* ── App bar (matches design doc screen 4) ── */}
      <header className="ds-appbar">
        {onBack && (
          <button className="ds-iconbtn" aria-label="Back" onClick={onBack}>
            <svg viewBox="0 0 24 24"><path d="m14 6-6 6 6 6" /></svg>
          </button>
        )}
        <span className="ds-appbar__body">
          <b className="ds-appbar__title">My team</b>
          <span className="ds-appbar__meta">
            {team.isOwner ? "You are the captain" : "You play for this team"}
          </span>
        </span>
      </header>

      {/* ── Team card ── */}
      <article className="ds-card">
        <div style={{ display: "flex", alignItems: "center", gap: "var(--ds-space-4)" }}>
          <span
            className={`ds-avatar ds-avatar--lg ds-avatar--${color}`}
            style={{ width: 64, height: 64, fontSize: "var(--ds-text-xl)" }}
          >
            {initials(team.name)}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <b style={{
              display: "block",
              fontFamily: "var(--ds-font-display)",
              fontSize: "var(--ds-text-xl)",
              letterSpacing: "var(--ds-tracking-tight)",
            }}>
              {team.name}
            </b>
            <span className="ds-caption">{playerLabel}</span>
          </span>
        </div>

        {/* ── Action buttons ──
            Owner only. Invites and adding players resolve through `findTeam`, which stays
            scoped to the creator, so showing these to a member would offer a button that
            answers 404. A member sees the squad and nothing he cannot do. */}
        {team.isOwner && (
          <div style={{ display: "flex", gap: "var(--ds-space-2)", marginTop: "var(--ds-space-5)" }}>
            <button
              className="ds-btn ds-btn--secondary ds-btn--sm"
              style={{ flex: 1 }}
              onClick={() => setShowInvite(true)}
            >
              Share squad
            </button>
          </div>
        )}
      </article>

      {/* ── Squad heading ── */}
      <div style={{
        display: "flex",
        alignItems: "baseline",
        gap: "var(--ds-space-3)",
        margin: "var(--ds-space-5) 0 var(--ds-space-2)",
      }}>
        <h2 className="ds-heading" style={{ flex: 1 }}>Squad</h2>
        <span className="ds-caption">{playerLabel}</span>
      </div>

      {/* ── Squad list ── */}
      {playerCount === 0 ? (
        <div className="ds-card ds-card--flat" style={{ textAlign: "center", padding: "var(--ds-space-8)" }}>
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="var(--ds-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "var(--ds-space-3)" }}>
            <circle cx="9.2" cy="8" r="3.3" />
            <path d="M3 20a6.2 6.2 0 0 1 12.4 0" />
            <path d="M19 8v6M16 11h6" />
          </svg>
          <p className="ds-caption">No players yet</p>
          <p className="ds-caption" style={{ marginTop: "var(--ds-space-1)" }}>
            Share your squad link to invite players
          </p>
        </div>
      ) : (
        <div className="ds-list">
          {team.squad.map((player, i) => {
            const c = AVATAR_COLORS[i % AVATAR_COLORS.length]!;
            return (
              <button
                className={`ds-list-item${justAdded === player.id ? " squad-row--new" : ""}`}
                key={player.id}
                data-player-id={player.id}
                onClick={() => setOpenPlayer(player)}
              >
                <span className={`ds-avatar ds-avatar--${c}`}>
                  {initials(player.displayName)}
                </span>
                <span className="ds-list-item__body">
                  <b className="ds-list-item__title">{player.displayName}</b>
                  {(player.phone || player.email) && (
                    <span className="ds-list-item__meta">{player.phone ?? player.email}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Add player CTA (primary, full-width, matches design doc) ── */}
      {team.isOwner && (
        <button
          className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block"
          style={{ marginTop: "var(--ds-space-5)" }}
          onClick={() => setShowAddPlayer(true)}
        >
          <span className="ds-btn__icon">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
          Add player
        </button>
      )}

      {/* ── Invite QR sheet (from Share squad) ── */}
      {showInvite && (
        <InviteQRSheet
          teamId={team.id}
          teamName={team.name}
          onClose={() => setShowInvite(false)}
        />
      )}

      {/* ── Add player sheet (3 tabs: invite, search, manual) ── */}
      {showAddPlayer && (
        <AddPlayerSheet
          teamId={team.id}
          teamName={team.name}
          existingPlayerIds={team.squad.map((p) => p.id)}
          existingNames={team.squad.map((p) => p.displayName)}
          onPlayerAdded={(player) => { setJustAdded(player.id); void load(true); }}
          onClose={() => { setShowAddPlayer(false); setJustAdded(null); }}
        />
      )}
      {/* Room to scroll the last rows above the add sheet, which leaves the page usable. */}
      {showAddPlayer && <div aria-hidden="true" style={{ height: "40dvh" }} />}
    </div>
  );
}
